import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import BatteriesTable from "./BatteriesTable";
import AddDevicesDialog from "./AddDevicesDialog";
import CommandHistoryDialog from "./CommandHistoryDialog";

const EMPTY_FILTERS = { status: "all", controllable: "all", command_status: "all", online_status: "all" };

const uniqueOptions = (devices, field) =>
  [...new Set(devices.map((d) => d[field]).filter((v) => v != null && v !== ""))]
    .sort()
    .map((v) => ({ value: String(v), label: String(v) }));

export default function BatteriesPage({
  params,
  memberName,
  devices,
  total,
  isLoading,
  isFetchingMore,
  hasNext,
  doFetchBatteryDevices,
  doFetchMoreBatteryDevices,
  unassignedBatteries,
  doFetchUnassignedDevices,
  doFetchMoreUnassignedDevices,
  doAssignDevices,
  doResetUnassignedDevices,
}) {
  const memberID = params?.memberId;
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [addDevicesOpen, setAddDevicesOpen] = useState(false);
  const [commandHistoryDevice, setCommandHistoryDevice] = useState(null);

  const statusOptions = useMemo(() => uniqueOptions(devices || [], "status"), [devices]);
  const commandStatusOptions = useMemo(() => uniqueOptions(devices || [], "command_status"), [devices]);
  const onlineStatusOptions = useMemo(() => uniqueOptions(devices || [], "online_status"), [devices]);
  const controllableOptions = [
    { value: "true", label: "Yes" },
    { value: "false", label: "No" },
  ];

  const filteredDevices = useMemo(() => {
    if (!devices) return [];
    return devices.filter((d) => {
      if (filters.status !== "all" && String(d.status ?? "") !== filters.status) return false;
      if (filters.controllable !== "all" && String(d.controllable) !== filters.controllable) return false;
      if (filters.command_status !== "all" && String(d.command_status ?? "") !== filters.command_status) return false;
      if (filters.online_status !== "all" && (d.online_status || "Offline") !== filters.online_status) return false;
      return true;
    });
  }, [devices, filters]);

  const activeFilterCount = Object.values(filters).filter((v) => v !== "all").length;

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => setFilters(EMPTY_FILTERS);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
        <h4 style={{ margin: 0 }}>Batteries</h4>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{ color: "#666", fontSize: "14px" }}>
            {(devices || []).length} of {total} fetched
          </span>
          <button
            onClick={() => setShowFilters((v) => !v)}
            style={{
              padding: "5px 14px",
              fontSize: "13px",
              border: "1px solid #aaa",
              borderRadius: "4px",
              background: showFilters ? "#e8eaf6" : "#fff",
              cursor: "pointer",
              position: "relative",
            }}
          >
            Filters
            {activeFilterCount > 0 && (
              <span style={{
                marginLeft: "6px",
                background: "#3f51b5",
                color: "#fff",
                borderRadius: "10px",
                padding: "1px 7px",
                fontSize: "11px",
                fontWeight: "bold",
              }}>
                {activeFilterCount}
              </span>
            )}
          </button>
          <Tooltip title="Add Device">
            <IconButton
              aria-label="Add Device"
              size="medium"
              color="primary"
              onClick={() => setAddDevicesOpen(true)}
            >
              <AddCircleOutlineIcon fontSize="large" />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {showFilters && (
        <div style={{
          display: "flex",
          alignItems: "flex-end",
          gap: "16px",
          padding: "14px 16px",
          marginBottom: "12px",
          background: "#f5f5f5",
          borderRadius: "4px",
          border: "1px solid #e0e0e0",
        }}>
          <FilterSelect
            label="Status"
            value={filters.status}
            options={statusOptions}
            onChange={(v) => handleFilterChange("status", v)}
          />
          <FilterSelect
            label="Controllable"
            value={filters.controllable}
            options={controllableOptions}
            onChange={(v) => handleFilterChange("controllable", v)}
          />
          <FilterSelect
            label="Command Status"
            value={filters.command_status}
            options={commandStatusOptions}
            onChange={(v) => handleFilterChange("command_status", v)}
          />
          <FilterSelect
            label="Online Status"
            value={filters.online_status}
            options={onlineStatusOptions}
            onChange={(v) => handleFilterChange("online_status", v)}
          />
          {activeFilterCount > 0 && (
            <button
              onClick={handleClearFilters}
              style={{
                padding: "5px 12px",
                fontSize: "12px",
                border: "1px solid #aaa",
                borderRadius: "4px",
                background: "#fff",
                cursor: "pointer",
                alignSelf: "flex-end",
                marginBottom: "1px",
              }}
            >
              Clear
            </button>
          )}
        </div>
      )}

      {activeFilterCount > 0 && (
        <div style={{ fontSize: "12px", lineHeight: "20px", color: "#555", minHeight: "20px" }}>
          {filteredDevices.length} match{filteredDevices.length !== 1 ? "es" : ""} in{" "}
          {(devices || []).length} fetched
          {hasNext && (
            <span style={{ color: "#e65100", marginLeft: "6px" }}>
              — {total - (devices || []).length} more records not yet loaded, results may be incomplete
            </span>
          )}
        </div>
      )}

      <BatteriesTable
        memberID={memberID}
        devices={filteredDevices}
        fetchedCount={(devices || []).length}
        isLoading={isLoading}
        isFetchingMore={isFetchingMore}
        hasNext={hasNext}
        doFetchBatteryDevices={doFetchBatteryDevices}
        doFetchMoreBatteryDevices={doFetchMoreBatteryDevices}
        onOpenCommandHistory={setCommandHistoryDevice}
      />

      <CommandHistoryDialog
        open={!!commandHistoryDevice}
        device={commandHistoryDevice}
        onClose={() => setCommandHistoryDevice(null)}
      />

      <AddDevicesDialog
        open={addDevicesOpen}
        onClose={() => setAddDevicesOpen(false)}
        memberID={memberID}
        memberName={memberName}
        devices={unassignedBatteries?.devices || []}
        hasNext={unassignedBatteries?.hasNext || false}
        isLoading={unassignedBatteries?.isLoading || false}
        isFetchingMore={unassignedBatteries?.isFetchingMore || false}
        isAssigning={unassignedBatteries?.isAssigning || false}
        refreshRecommended={unassignedBatteries?.refreshRecommended || false}
        newerCount={unassignedBatteries?.newerCount || 0}
        doFetchUnassignedDevices={doFetchUnassignedDevices}
        doFetchMoreUnassignedDevices={doFetchMoreUnassignedDevices}
        doAssignDevices={doAssignDevices}
        doResetUnassignedDevices={doResetUnassignedDevices}
      />
    </div>
  );
}

const FilterSelect = ({ label, value, options, onChange }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: "150px" }}>
    <label style={{ fontSize: "12px", color: "#555", fontWeight: 500 }}>{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ padding: "5px 8px", fontSize: "13px", border: "1px solid #ccc", borderRadius: "4px" }}
    >
      <option value="all">All</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

BatteriesPage.propTypes = {
  params: PropTypes.shape({
    memberId: PropTypes.any,
  }),
  memberName: PropTypes.string,
  devices: PropTypes.array,
  total: PropTypes.number,
  isLoading: PropTypes.bool,
  isFetchingMore: PropTypes.bool,
  hasNext: PropTypes.bool,
  doFetchBatteryDevices: PropTypes.func.isRequired,
  doFetchMoreBatteryDevices: PropTypes.func.isRequired,
  unassignedBatteries: PropTypes.object,
  doFetchUnassignedDevices: PropTypes.func.isRequired,
  doFetchMoreUnassignedDevices: PropTypes.func.isRequired,
  doAssignDevices: PropTypes.func.isRequired,
  doResetUnassignedDevices: PropTypes.func.isRequired,
};